// ==========================================================
// Color Science & Subtractive Pigment Mixing Engine
// ==========================================================

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface Lab {
  l: number;
  a: number;
  b: number;
}

export interface PigmentRecipe {
  cyan: number;
  magenta: number;
  yellow: number;
  black: number;
  white: number;
}

export class ColorMixer {
  /**
   * Convert Hex string (#RRGGBB) to RGB object
   */
  public static hexToRgb(hex: string): RGB {
    const cleanHex = hex.replace('#', '');
    const bigint = parseInt(cleanHex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  }

  /**
   * Convert RGB object to Hex string
   */
  public static rgbToHex(rgb: RGB): string {
    const toHex = (c: number) => {
      const hex = Math.round(Math.min(255, Math.max(0, c))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
  }

  /**
   * Subtractive Color Mixing Simulation (CMYK + White)
   * Simulates real paint pigment blending with opacity and spectral absorption
   */
  public static mixPigments(recipe: PigmentRecipe): RGB {
    const { cyan, magenta, yellow, black, white } = recipe;
    const total = cyan + magenta + yellow + black + white;

    if (total === 0) {
      return { r: 255, g: 255, b: 255 }; // Default blank dish is white
    }

    // Normalized ratios
    const cRatio = cyan / total;
    const mRatio = magenta / total;
    const yRatio = yellow / total;
    const kRatio = black / total;
    const wRatio = white / total;

    // Subtractive mixing in CMY space
    // Base CMY starting from non-white pigments
    let c = cRatio;
    let m = mRatio;
    let y = yRatio;

    // Darkening from Black (K)
    c = c + kRatio * (1 - c);
    m = m + kRatio * (1 - m);
    y = y + kRatio * (1 - y);

    // Lightening / Tinting from White (W)
    c = c * (1 - wRatio * 0.92);
    m = m * (1 - wRatio * 0.92);
    y = y * (1 - wRatio * 0.92);

    // Convert CMY back to RGB with realistic pigment gamma curve
    const gamma = 1.15;
    const r = Math.round(255 * Math.pow(Math.max(0, 1 - c), gamma));
    const g = Math.round(255 * Math.pow(Math.max(0, 1 - m), gamma));
    const b = Math.round(255 * Math.pow(Math.max(0, 1 - y), gamma));

    return { r, g, b };
  }

  /**
   * RGB to CIE-L*a*b* conversion for perceptual color distance
   */
  public static rgbToLab(rgb: RGB): Lab {
    // 1. RGB to linear sRGB
    let r = rgb.r / 255;
    let g = rgb.g / 255;
    let b = rgb.b / 255;

    r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
    g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
    b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

    // 2. sRGB to XYZ (D65 standard illuminant)
    let x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
    let y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1.00000;
    let z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;

    const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);

    const fx = f(x);
    const fy = f(y);
    const fz = f(z);

    return {
      l: 116 * fy - 16,
      a: 500 * (fx - fy),
      b: 200 * (fy - fz)
    };
  }

  /**
   * Calculate CIE76/94 Delta E (Color Difference)
   * Lower Delta E means closer color (0 = identical, <2 = indistinguishable to human eye)
   */
  public static calculateDeltaE(rgb1: RGB, rgb2: RGB): number {
    const lab1 = this.rgbToLab(rgb1);
    const lab2 = this.rgbToLab(rgb2);

    const dl = lab1.l - lab2.l;
    const da = lab1.a - lab2.a;
    const db = lab1.b - lab2.b;

    return Math.sqrt(dl * dl + da * da + db * db);
  }

  /**
   * Calculate human-friendly match score percentage (0% - 100%)
   */
  public static calculateMatchPercentage(targetHex: string, currentRgb: RGB): {
    percentage: number;
    deltaE: number;
    rating: string;
  } {
    const targetRgb = this.hexToRgb(targetHex);
    const deltaE = this.calculateDeltaE(targetRgb, currentRgb);

    // Delta E of 0 -> 100%, Delta E of 50+ -> 0%
    const score = Math.max(0, Math.min(100, Math.round(100 - deltaE * 1.6)));

    let rating = '要調合';
    if (score >= 97) rating = '神技・塗聖級 👑';
    else if (score >= 90) rating = '免許皆伝 🌟';
    else if (score >= 80) rating = '熟練職人 ✨';
    else if (score >= 65) rating = '一人前 👍';
    else if (score >= 40) rating = '見習い 🤔';

    return {
      percentage: score,
      deltaE,
      rating
    };
  }
}
